/**
 * 疑似重复订单确认弹层（四端同一套机制的 web 实现）
 *
 * 后端只提示、裁定的是人：命中时返回 needConfirm='slot_duplicate' + 候选单，
 * 这里给的是「核对 + 两个出口」，不是一句 toast——否则用户只会对着重新弹出的空白表单
 * 反复点同一个提交按钮。表单弹窗（新建/编辑）、详情抽屉（改档期、改派客户）共用这一份。
 */
import type { SlotDuplicateHintItem } from '../../../../utils/errorHandler';

interface Props {
  message: string;
  duplicates: SlotDuplicateHintItem[];
  /** 重发在途时禁用「继续提交」，避免连点出两张单 */
  busy: boolean;
  /** 出口一（判为重复）的文案随场景变：新建是「去驳回」，编辑/改派是「放弃本次修改」 */
  keepLabel: string;
  continueLabel: string;
  onKeep: () => void;
  onContinue: () => void;
}

export default function SlotDuplicateDialog({
  message,
  duplicates,
  busy,
  keepLabel,
  continueLabel,
  onKeep,
  onContinue,
}: Props) {
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-lg max-h-[80vh] flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-start gap-3 shrink-0">
          <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
            <i className="ri-file-shield-2-line text-amber-500 text-lg" />
          </div>
          <div>
            <p className="text-base font-semibold text-gray-900">可能存在重复订单</p>
            <p className="text-sm text-gray-500 mt-1 leading-relaxed">{message}</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {duplicates.map((d) => (
            <div key={d.orderId} className="border border-gray-100 rounded-xl p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-sm text-gray-900">{d.orderNumber}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${d.submittedByMe ? 'bg-gray-100 text-gray-600' : 'bg-teal-50 text-teal-700'}`}>
                  {d.submittedByMe ? '我提交的' : `${d.submittedBy} 提交的`}
                </span>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-500">
                <span>客户：{d.customerName || '-'}</span>
                <span>预约日期：{d.orderDate || '-'}</span>
                {/* 时间段与「和本次是否重叠」都取服务端算好的值（order-slot-time 唯一实现），本端只负责显示 */}
                <span className="flex items-center gap-1">
                  时间段：{d.timeLabel || '-'}
                  {d.timeOverlap === true && (
                    <span className="bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded whitespace-nowrap">与本次重叠</span>
                  )}
                </span>
                <span>订单金额：¥{Number(d.orderAmount ?? 0).toFixed(2)}</span>
                <span>已付：¥{Number(d.paidAmount ?? 0).toFixed(2)}</span>
                <span>提交时间：{d.submittedAt || '-'}</span>
                <span>审核状态：{d.approvalStatus || '-'}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 shrink-0 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <button
            onClick={onKeep}
            className="flex-1 px-4 py-2 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            {keepLabel}
          </button>
          <button
            onClick={onContinue}
            disabled={busy}
            className="flex-1 px-4 py-2 text-sm bg-teal-500 hover:bg-teal-600 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {continueLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
